import React, { useState, useEffect, useCallback } from "react";
import { api } from "./api";
import PasswordInput from "./PasswordInput";
import ContourMap from "./ContourMap";
import { DevCredit } from "./DeveloperCard";

// ── Design tokens ─────────────────────────────────────────────
const C = {
  navy: "#0f1f3d", blue: "#1a73e8", danger: "#e74c3c",
  success: "#27ae60", warn: "#f39c12", purple: "#8e44ad",
  bg: "#eef1f8", border: "#dde1e7", text: "#2c3e50", muted: "#8a97aa",
};

const GRAD = {
  navy:    "linear-gradient(135deg, #0f1f3d 0%, #1a3a6b 100%)",
  blue:    "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
  orange:  "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)",
  green:   "linear-gradient(135deg, #27ae60 0%, #1a7a3f 100%)",
  purple:  "linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)",
  danger:  "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
};

const S = {
  page: { minHeight: "100vh", background: C.bg, fontFamily: "'Segoe UI', system-ui, sans-serif" },

  // Header
  header: {
    background: GRAD.navy,
    color: "#fff", padding: "0 32px",
    display: "grid", gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    height: 72, boxShadow: "0 4px 24px rgba(15,31,61,0.45)",
    position: "sticky", top: 0, zIndex: 100,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 14 },
  headerCenter: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" },
  headerLogo: {
    width: 36, height: 36, borderRadius: 8,
    background: "rgba(255,255,255,0.15)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 18,
  },
  headerTitle: { margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: "-0.3px" },
  headerSub: { fontSize: 10, color: "rgba(255,255,255,0.45)", letterSpacing: "0.4px" },
  headerRight: { display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-end" },
  badge: {
    background: "rgba(231,76,60,0.9)", color: "#fff",
    borderRadius: 5, padding: "3px 9px", fontSize: 10, fontWeight: 800, letterSpacing: "0.5px",
  },
  userPill: {
    background: "rgba(255,255,255,0.12)", borderRadius: 20,
    padding: "5px 12px 5px 8px", display: "flex", alignItems: "center", gap: 7, fontSize: 13,
  },
  avatar: {
    width: 26, height: 26, borderRadius: "50%",
    background: "rgba(255,255,255,0.25)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
  },
  logoutBtn: {
    background: "rgba(255,255,255,0.12)", color: "#fff",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600,
  },

  // Body
  body: { padding: "28px 32px", maxWidth: 1360, margin: "0 auto" },

  // Stat cards
  statCard: (grad) => ({
    background: grad, borderRadius: 14, padding: "22px 24px",
    boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
    position: "relative", overflow: "hidden", cursor: "default",
  }),
  statLabel: { margin: 0, fontSize: 11, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.7px" },
  statValue: { margin: "8px 0 0", fontSize: 38, fontWeight: 800, color: "#fff", lineHeight: 1 },
  statIcon: { position: "absolute", right: 18, bottom: 14, fontSize: 42, opacity: 0.18 },
  statGlow: {
    position: "absolute", top: -30, right: -30, width: 100, height: 100,
    borderRadius: "50%", background: "rgba(255,255,255,0.08)",
  },

  // Tab nav
  tabBar: {
    background: "#fff", borderRadius: 12, padding: "5px",
    display: "flex", gap: 3, marginBottom: 24,
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
  },
  tab: (active) => ({
    flex: 1, padding: "10px 16px", border: "none", cursor: "pointer",
    borderRadius: 9, fontSize: 13, fontWeight: active ? 700 : 500,
    background: active ? GRAD.blue : "transparent",
    color: active ? "#fff" : C.muted,
    boxShadow: active ? "0 4px 14px rgba(26,115,232,0.35)" : "none",
    transition: "all 0.18s",
  }),

  // Section cards
  section: {
    background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)", marginBottom: 24, overflow: "hidden",
  },
  sectionHead: {
    padding: "16px 24px", borderBottom: `1px solid ${C.border}`,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "#fdfdfe",
  },
  sectionTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: C.navy },

  // Table
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 700,
    color: C.muted, textTransform: "uppercase", letterSpacing: "0.4px",
    borderBottom: `2px solid ${C.border}`, background: "#f8f9fc",
  },
  td: { padding: "12px 16px", fontSize: 13.5, color: C.text, borderBottom: `1px solid #f0f2f6` },

  // Buttons
  btn: (grad = GRAD.blue) => ({
    background: grad, color: "#fff", border: "none", borderRadius: 8,
    padding: "8px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600,
    boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
  }),
  outlineBtn: {
    background: "#fff", color: C.text, border: `1px solid ${C.border}`,
    borderRadius: 7, padding: "6px 13px", cursor: "pointer", fontSize: 13,
  },
  dangerOutlineBtn: {
    background: "#fff", color: C.danger, border: `1px solid ${C.danger}44`,
    borderRadius: 7, padding: "6px 13px", cursor: "pointer", fontSize: 13,
  },

  // Tags
  tag: (color) => ({
    background: color + "18", color, borderRadius: 5,
    padding: "2px 9px", fontSize: 11, fontWeight: 700, letterSpacing: "0.3px",
  }),
};

// ── Modal ─────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth = 520 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,31,61,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000, padding: 16, backdropFilter: "blur(2px)" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", width: "100%", maxWidth, boxShadow: "0 32px 80px rgba(0,0,0,0.3)", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <h3 style={{ margin: 0, color: C.navy, fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "#f0f2f6", border: "none", borderRadius: 7, width: 30, height: 30, cursor: "pointer", color: C.muted, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Form helpers ──────────────────────────────────────────────
const FL = ({ label, col, children }) => (
  <div style={{ marginBottom: 14, gridColumn: col === 2 ? "1 / -1" : undefined }}>
    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} style={{ width: "100%", padding: "10px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing: "border-box", outline: "none", background: "#fafbfd" }} />
);

const Select = ({ children, ...props }) => (
  <select {...props} style={{ width: "100%", padding: "10px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, background: "#fafbfd" }}>
    {children}
  </select>
);

// ── System section header ─────────────────────────────────────
function SystemSectionHead({ title, sub }) {
  return (
    <div style={{ padding: "14px 22px", background: "linear-gradient(90deg, #0f1f3d08, transparent)", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: GRAD.navy, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>⚙</div>
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.navy }}>{title}</p>
          {sub && <p style={{ margin: 0, fontSize: 11, color: C.muted }}>{sub}</p>}
        </div>
      </div>
      <span style={{ background: C.navy + "14", color: C.navy, borderRadius: 5, padding: "2px 9px", fontSize: 10, fontWeight: 800, letterSpacing: "0.5px" }}>INTERNAL</span>
    </div>
  );
}

// ── LetterheadBoxDefiner ──────────────────────────────────────
// Shows the letterhead PNG at A4 proportions. Admin drags to draw
// the text-area box. Box coords stored as % of the preview container.
function LetterheadBoxDefiner({ png, box, onChange }) {
  const containerRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const [start, setStart] = React.useState(null);
  const [current, setCurrent] = React.useState(null);

  // A4 ratio: 210 × 297 mm → height = width × (297/210)
  const A4_RATIO = 297 / 210;

  const getRelPos = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top)  / rect.height)),
    };
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    const pos = getRelPos(e);
    setStart(pos); setCurrent(pos); setDragging(true);
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    setCurrent(getRelPos(e));
  };

  const onMouseUp = (e) => {
    if (!dragging || !start) return;
    setDragging(false);
    const end = getRelPos(e);
    const left   = Math.min(start.x, end.x);
    const top    = Math.min(start.y, end.y);
    const width  = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);
    if (width > 0.02 && height > 0.02) {
      onChange({ top: +(top*100).toFixed(2), left: +(left*100).toFixed(2), width: +(width*100).toFixed(2), height: +(height*100).toFixed(2) });
    }
    setStart(null); setCurrent(null);
  };

  // Compute live drag rect
  const dragRect = dragging && start && current ? {
    left:   `${Math.min(start.x, current.x) * 100}%`,
    top:    `${Math.min(start.y, current.y) * 100}%`,
    width:  `${Math.abs(current.x - start.x) * 100}%`,
    height: `${Math.abs(current.y - start.y) * 100}%`,
  } : null;

  return (
    <div>
      <div style={{ fontSize: 11, color: "#555", marginBottom: 8, padding: "6px 10px", background: "#fffbe6", border: "1px solid #f0d060", borderRadius: 6 }}>
        💡 <strong>Drag on the preview</strong> to draw the text area box. The cover letter text will be placed exactly within that box.
        {box && <span style={{ marginLeft: 8, color: "#27ae60", fontWeight: 600 }}>✓ Box defined: top {box.top}%, left {box.left}%, {box.width}% × {box.height}%</span>}
      </div>

      {/* A4-proportioned preview container */}
      <div
        ref={containerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={{
          position: "relative", width: "100%",
          paddingBottom: `${A4_RATIO * 100}%`,
          background: "#fff", border: "2px solid #1a73e8",
          borderRadius: 6, overflow: "hidden", cursor: "crosshair",
          userSelect: "none",
        }}
      >
        {/* Letterhead image — contain preserves aspect ratio exactly as uploaded */}
        <img src={png} alt="Letterhead"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain", objectPosition: "top center", display: "block", pointerEvents: "none" }}
        />

        {/* Saved box */}
        {box && !dragging && (
          <div style={{
            position: "absolute",
            top: `${box.top}%`, left: `${box.left}%`,
            width: `${box.width}%`, height: `${box.height}%`,
            border: "2.5px solid #1a73e8",
            background: "rgba(26,115,232,0.10)",
            boxSizing: "border-box", pointerEvents: "none",
          }}>
            <div style={{ position: "absolute", top: 2, left: 4, fontSize: 9, fontWeight: 700, color: "#1a73e8", background: "rgba(255,255,255,0.85)", padding: "1px 4px", borderRadius: 3, whiteSpace: "nowrap" }}>
              TEXT AREA
            </div>
          </div>
        )}

        {/* Live drag rect */}
        {dragRect && (
          <div style={{
            position: "absolute", ...dragRect,
            border: "2px dashed #e74c3c",
            background: "rgba(231,76,60,0.08)",
            boxSizing: "border-box", pointerEvents: "none",
          }} />
        )}

        {/* Corner labels */}
        <div style={{ position: "absolute", top: 4, left: 6, fontSize: 9, color: "rgba(0,0,0,0.3)", pointerEvents: "none" }}>A4 Top-Left</div>
        <div style={{ position: "absolute", bottom: 4, right: 6, fontSize: 9, color: "rgba(0,0,0,0.3)", pointerEvents: "none" }}>A4 Bottom-Right</div>
      </div>
    </div>
  );
}


// ── Main Dashboard ────────────────────────────────────────────
export default function SuperUserDashboard({ user, onLogout }) {
  const [companies, setCompanies]   = useState([]);
  const [users, setUsers]           = useState([]);
  const [tab, setTab]               = useState("companies");
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [companySearch, setCompanySearch]     = useState("");

  const [stats, setStats]           = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsFilter, setStatsFilter]   = useState("");
  // Expanded company reports in the breakdown table
  const [expandedCompanyCode, setExpandedCompanyCode]     = useState(null);
  const [companyReports, setCompanyReports]               = useState([]);
  const [companyReportsLoading, setCompanyReportsLoading] = useState(false);
  const [companyBreakdownSearch, setCompanyBreakdownSearch] = useState("");

  const [modal, setModal]   = useState(null);
  const [target, setTarget] = useState(null);
  const [form, setForm]     = useState({});
  const [error, setError]   = useState("");
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    const [c, u] = await Promise.all([api.listCompanies(), api.listUsers()]);
    setCompanies(c); setUsers(u);
  }, []);

  const loadStats = useCallback(async (companyCode = "") => {
    setStatsLoading(true);
    try {
      const params = companyCode ? { company_code: companyCode } : {};
      const data = await api.getReportStats(params);
      setStats(data);
    } catch (e) { console.error(e); }
    finally { setStatsLoading(false); }
  }, []);

  const loadCompanyReports = useCallback(async (companyCode) => {
    setCompanyReportsLoading(true);
    try {
      const data = await api.listAdminReports({ company_code: companyCode, limit: 200 });
      setCompanyReports(data.reports || []);
    } catch (e) { console.error(e); setCompanyReports([]); }
    finally { setCompanyReportsLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (tab === "reports") loadStats(statsFilter); }, [tab, statsFilter, loadStats]);

  const openModal = (name, row = null) => {
    setError(""); setTarget(row);
    if (row) {
      // Parse letterhead_text_box from JSON string if needed
      let parsed = { ...row };
      if (typeof parsed.letterhead_text_box === "string" && parsed.letterhead_text_box) {
        try { parsed.letterhead_text_box = JSON.parse(parsed.letterhead_text_box); } catch(_) { parsed.letterhead_text_box = null; }
      }
      setForm(parsed);
    } else {
      setForm({});
    }
    setModal(name);
  };
  const closeModal = () => { setModal(null); setTarget(null); setForm({}); setError(""); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveCompany = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      // Serialize letterhead_text_box to JSON string for storage
      const payload = { ...form };
      if (payload.letterhead_text_box && typeof payload.letterhead_text_box === "object") {
        payload.letterhead_text_box = JSON.stringify(payload.letterhead_text_box);
      }
      modal === "addCompany" ? await api.createCompany(payload) : await api.updateCompany(target.id, payload);
      await loadData(); closeModal();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const deleteCompany = async () => {
    setSaving(true); setError("");
    try { await api.deleteCompany(target.id); await loadData(); closeModal(); }
    catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const saveUser = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      modal === "addUser" ? await api.createUser(form) : await api.updateUser(target.id, form);
      await loadData(); closeModal();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const deleteUser = async () => {
    setSaving(true); setError("");
    try { await api.deleteUser(target.id); await loadData(); closeModal(); }
    catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const roleColor = (r) => ({ super_user: C.danger, admin: C.warn, user: C.blue }[r] || C.muted);

  const systemCompany   = companies.find(c => c.company_code === "SYSTEM");
  const clientCompanies = companies.filter(c => c.company_code !== "SYSTEM");
  const filteredCompanies = clientCompanies.filter(c => {
    const q = companySearch.toLowerCase();
    return !q || c.company_code.toLowerCase().includes(q) || (c.company_name || "").toLowerCase().includes(q);
  });
  const usersByCompany = filteredCompanies.map(c => ({
    ...c, members: users.filter(u => u.company_code === c.company_code),
  }));
  const systemUsers = users.filter(u => u.company_code === "SYSTEM");
  const toggleCompany = (code) => setExpandedCompany(prev => prev === code ? null : code);

  // ── KPI data ────────────────────────────────────────────────
  const kpis = [
    { label: "Total Companies",  value: clientCompanies.length,                                  grad: GRAD.navy,   icon: "🏢" },
    { label: "Total Users",      value: users.filter(u => u.company_code !== "SYSTEM").length,   grad: GRAD.blue,   icon: "👥" },
    { label: "Admins",           value: users.filter(u => u.role === "admin").length,            grad: GRAD.orange, icon: "👑" },
    { label: "Total Reports",    value: stats?.totals?.total ?? "—", onClick: () => setTab("reports"), grad: GRAD.green, icon: "📋" },
  ];

  const tabs = [
    ["companies", "🏢", "Companies"],
    ["users",     "👥", "All Users"],
    ["reports",   "📊", "Reports"],
    ["map",       "🗺",  "Map"],
  ];

  return (
    <div style={S.page}>

      {/* ── Header ── */}
      <div style={S.header}>
        {/* Left: logo + title */}
        <div style={S.headerLeft}>
          <div style={S.headerLogo}>⚙</div>
          <div>
            <p style={S.headerTitle}>System Administration</p>
          </div>
        </div>

        {/* Center: developer credit */}
        <div style={S.headerCenter}>
          <DevCredit
            style={{ color: "#fff" }}
            textStyle={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.2px" }}
          />
        </div>

        {/* Right: badge + user + logout */}
        <div style={S.headerRight}>
          <span style={S.badge}>SUPER ADMIN</span>
          <div style={S.userPill}>
            <div style={S.avatar}>👤</div>
            <span>{user.username}</span>
          </div>
          <button style={S.logoutBtn} onClick={onLogout}>Sign Out</button>
        </div>
      </div>

      <div style={S.body}>

        {/* ── KPI Cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18, marginBottom: 26 }}>
          {kpis.map(({ label, value, grad, icon, onClick }) => (
            <div key={label} onClick={onClick} style={{ ...S.statCard(grad), cursor: onClick ? "pointer" : "default" }}>
              <div style={S.statGlow} />
              <p style={S.statLabel}>{label}</p>
              <p style={S.statValue}>{value}</p>
              <span style={S.statIcon}>{icon}</span>
            </div>
          ))}
        </div>

        {/* ── Tab Navigation ── */}
        <div style={S.tabBar}>
          {tabs.map(([t, icon, label]) => (
            <button key={t} onClick={() => setTab(t)} style={S.tab(tab === t)}>
              {icon} {label}
            </button>
          ))}
        </div>

        {/* ══════════════ Companies Tab ══════════════ */}
        {tab === "companies" && (<>

          {/* System Administration card — top */}
          {systemCompany && (
            <div style={{ ...S.section, border: `1.5px solid ${C.navy}20`, marginBottom: 18 }}>
              <SystemSectionHead title="System Administration" sub="Super admin accounts only — not a client company" />
              <table style={S.table}>
                <thead><tr>{["Code","Name","Email","Phone","Super Users","Created"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  <tr style={{ background: "#f8f9fc" }}>
                    <td style={S.td}><code style={{ background: "#fdecea", color: C.danger, padding: "2px 8px", borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{systemCompany.company_code}</code></td>
                    <td style={S.td}><strong>{systemCompany.company_name}</strong></td>
                    <td style={S.td}>{systemCompany.contact_email || <span style={{ color: C.muted }}>—</span>}</td>
                    <td style={S.td}>{systemCompany.contact_phone || <span style={{ color: C.muted }}>—</span>}</td>
                    <td style={S.td}><span style={S.tag(C.danger)}>{systemCompany.user_count} users</span></td>
                    <td style={{ ...S.td, color: C.muted, fontSize: 12 }}>{systemCompany.created_at?.slice(0,10)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Client companies */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <div>
                <h2 style={S.sectionTitle}>Companies</h2>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
                  {filteredCompanies.length}{companySearch ? ` of ${clientCompanies.length}` : ""} registered
                </p>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 13 }}>🔍</span>
                  <input
                    type="text" placeholder="Search code or name…"
                    value={companySearch} onChange={e => setCompanySearch(e.target.value)}
                    style={{ padding: "8px 32px 8px 34px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, width: 210, outline: "none", background: "#fafbfd" }}
                  />
                  {companySearch && (
                    <button onClick={() => setCompanySearch("")} style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.muted, lineHeight: 1 }}>✕</button>
                  )}
                </div>
                <button style={S.btn()} onClick={() => openModal("addCompany")}>+ Add Company</button>
              </div>
            </div>
            <table style={S.table}>
              <thead>
                <tr>{["Code","Company Name","Email","Phone","Users","Created","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filteredCompanies.map((c, i) => (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfd" }}>
                    <td style={S.td}><code style={{ background: "#edf2ff", color: C.blue, padding: "2px 8px", borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{c.company_code}</code></td>
                    <td style={S.td}><strong style={{ color: C.navy }}>{c.company_name || <span style={{ color: C.muted, fontWeight: 400 }}>—</span>}</strong></td>
                    <td style={{ ...S.td, color: C.muted }}>{c.contact_email || "—"}</td>
                    <td style={{ ...S.td, color: C.muted }}>{c.contact_phone || "—"}</td>
                    <td style={S.td}>
                      <span style={{ ...S.tag(C.blue), cursor: "pointer" }} onClick={() => { setTab("users"); setExpandedCompany(c.company_code); }}>
                        {c.user_count} users
                      </span>
                    </td>
                    <td style={{ ...S.td, color: C.muted, fontSize: 12 }}>{c.created_at?.slice(0,10)}</td>
                    <td style={S.td}>
                      <div style={{ display: "flex", gap: 7 }}>
                        <button style={S.outlineBtn} onClick={() => openModal("editCompany", c)}>Edit</button>
                        <button style={S.dangerOutlineBtn} onClick={() => openModal("deleteCompany", c)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredCompanies.length === 0 && (
                  <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: C.muted, padding: 40 }}>
                    {companySearch ? `No companies match "${companySearch}"` : 'No companies yet — click "+ Add Company"'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>)}

        {/* ══════════════ Users Tab ══════════════ */}
        {tab === "users" && (<>

          {/* System users — top */}
          <div style={{ ...S.section, border: `1.5px solid ${C.navy}20`, marginBottom: 18 }}>
            <SystemSectionHead title="System Administration Users" sub="Super admin accounts — not part of any client company" />
            {systemUsers.length === 0 ? (
              <p style={{ padding: "20px 24px", color: C.muted, fontSize: 13 }}>No system users found.</p>
            ) : (
              <table style={S.table}>
                <thead><tr>{["Username","Email","Role","Status","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {systemUsers.map((u, i) => (
                    <tr key={u.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfd" }}>
                      <td style={S.td}><strong>{u.username}</strong></td>
                      <td style={{ ...S.td, color: C.muted }}>{u.email || "—"}</td>
                      <td style={S.td}><span style={S.tag(C.danger)}>{u.role.replace("_"," ").toUpperCase()}</span></td>
                      <td style={S.td}>{u.must_change_password ? <span style={S.tag(C.warn)}>TEMP PWD</span> : <span style={S.tag(C.success)}>ACTIVE</span>}</td>
                      <td style={S.td}>
                        <div style={{ display: "flex", gap: 7 }}>
                          <button style={S.outlineBtn} onClick={() => openModal("editUser", u)}>Edit</button>
                          <button style={S.dangerOutlineBtn} onClick={() => openModal("deleteUser", u)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Client accordion */}
          <div style={S.section}>
            <div style={S.sectionHead}>
              <div>
                <h2 style={S.sectionTitle}>All Users by Company</h2>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{users.filter(u => u.company_code !== "SYSTEM").length} users across {clientCompanies.length} companies</p>
              </div>
              <button style={S.btn()} onClick={() => openModal("addUser")}>+ Add User</button>
            </div>

            {usersByCompany.length === 0 && (
              <p style={{ padding: "32px 24px", textAlign: "center", color: C.muted }}>No companies found.</p>
            )}

            {usersByCompany.map(group => {
              const isOpen = expandedCompany === group.company_code;
              return (
                <div key={group.company_code}>
                  {/* Accordion header */}
                  <div onClick={() => toggleCompany(group.company_code)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 22px", cursor: "pointer",
                    background: isOpen ? "#edf5ff" : "#fff",
                    borderBottom: `1px solid ${C.border}`,
                    transition: "background 0.15s",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: isOpen ? GRAD.blue : "#f0f2f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: isOpen ? "#fff" : C.muted, transition: "all 0.15s" }}>
                        {isOpen ? "▼" : "▶"}
                      </div>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: 14, color: isOpen ? C.blue : C.navy }}>{group.company_name || group.company_code}</span>
                        <code style={{ marginLeft: 9, background: "#edf2ff", color: C.muted, padding: "1px 7px", borderRadius: 4, fontSize: 11 }}>{group.company_code}</code>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {group.contact_email && <span style={{ fontSize: 12, color: C.muted }}>✉ {group.contact_email}</span>}
                      <span style={S.tag(C.blue)}>{group.members.length} user{group.members.length !== 1 ? "s" : ""}</span>
                    </div>
                  </div>

                  {/* Expanded */}
                  {isOpen && (
                    <div style={{ background: "#f6f9ff", borderBottom: `2px solid #1a73e820` }}>
                      <div style={{ padding: "10px 22px 8px 62px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                        <h3 style={{ margin: 0, fontSize: 13, color: C.navy, fontWeight: 700 }}>Users of {group.company_name || group.company_code}</h3>
                        <span style={S.tag(C.blue)}>{group.members.length}</span>
                      </div>
                      {group.members.length === 0 ? (
                        <p style={{ padding: "16px 62px", color: C.muted, fontSize: 13 }}>No users yet.</p>
                      ) : (
                        <table style={{ ...S.table, background: "transparent" }}>
                          <thead><tr>{["Username","Email","Role","Status","Actions"].map(h=><th key={h} style={{ ...S.th, paddingLeft: h === "Username" ? 62 : 16, background: "transparent" }}>{h}</th>)}</tr></thead>
                          <tbody>
                            {group.members.map(u => (
                              <tr key={u.id}>
                                <td style={{ ...S.td, paddingLeft: 62 }}><strong>{u.username}</strong></td>
                                <td style={{ ...S.td, color: C.muted }}>{u.email || "—"}</td>
                                <td style={S.td}><span style={S.tag(roleColor(u.role))}>{u.role.replace("_"," ").toUpperCase()}</span></td>
                                <td style={S.td}>{u.must_change_password ? <span style={S.tag(C.warn)}>TEMP PWD</span> : <span style={S.tag(C.success)}>ACTIVE</span>}</td>
                                <td style={S.td}>
                                  <div style={{ display: "flex", gap: 7 }}>
                                    <button style={S.outlineBtn} onClick={() => openModal("editUser", u)}>Edit</button>
                                    <button style={S.dangerOutlineBtn} onClick={() => openModal("deleteUser", u)}>Delete</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>)}

        {/* ══════════════ Reports Tab ══════════════ */}
        {tab === "reports" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.muted }}>Filter by Company:</label>
              <select value={statsFilter} onChange={e => setStatsFilter(e.target.value)}
                style={{ padding: "8px 13px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, minWidth: 220, background: "#fff" }}>
                <option value="">All Companies</option>
                {clientCompanies.map(c => <option key={c.company_code} value={c.company_code}>{c.company_code} — {c.company_name || "—"}</option>)}
              </select>
              <button style={S.outlineBtn} onClick={() => loadStats(statsFilter)}>↺ Refresh</button>
            </div>

            {statsLoading ? (
              <div style={{ textAlign: "center", padding: 56, color: C.muted, fontSize: 15 }}>Loading statistics…</div>
            ) : !stats ? (
              <div style={{ textAlign: "center", padding: 56, color: C.muted }}>No data available.</div>
            ) : (
              <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18, marginBottom: 24 }}>
                {[
                  { label: "Total Reports",       value: stats.totals?.total ?? 0,       grad: GRAD.navy,   icon: "📋" },
                  { label: "Preliminary Reports",  value: stats.totals?.preliminary ?? 0, grad: GRAD.blue,   icon: "📝" },
                  { label: "Final Reports",        value: stats.totals?.final ?? 0,       grad: GRAD.green,  icon: "✅" },
                ].map(({ label, value, grad, icon }) => (
                  <div key={label} style={S.statCard(grad)}>
                    <div style={S.statGlow} />
                    <p style={S.statLabel}>{label}</p>
                    <p style={S.statValue}>{value}</p>
                    <span style={S.statIcon}>{icon}</span>
                  </div>
                ))}
              </div>

              <div style={S.section}>
                <div style={{ ...S.sectionHead, flexWrap: "wrap", gap: 10 }}>
                  <h2 style={S.sectionTitle}>📊 Report Breakdown by Company</h2>
                  <div style={{ position: "relative", marginLeft: "auto" }}>
                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14, pointerEvents: "none" }}>🔍</span>
                    <input
                      type="text"
                      placeholder="Search company code or name…"
                      value={companyBreakdownSearch}
                      onChange={e => setCompanyBreakdownSearch(e.target.value)}
                      style={{ padding: "7px 12px 7px 32px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, width: 240, outline: "none", background: "#fff" }}
                    />
                    {companyBreakdownSearch && (
                      <button onClick={() => setCompanyBreakdownSearch("")}
                        style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 14, lineHeight: 1 }}>✕</button>
                    )}
                  </div>
                </div>
                <table style={S.table}>
                  <thead><tr>{["Company Code","Company Name","Total","Preliminary","Final","Last Activity",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {stats.perCompany.length === 0 ? (
                      <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: C.muted, padding: 32 }}>No reports found.</td></tr>
                    ) : stats.perCompany
                        .filter(row => {
                          const q = companyBreakdownSearch.toLowerCase().trim();
                          if (!q) return true;
                          return (row.company_code || "").toLowerCase().includes(q) ||
                                 (row.company_name || "").toLowerCase().includes(q);
                        })
                        .map((row, i) => {
                      const isOpen = expandedCompanyCode === row.company_code;
                      return (
                        <React.Fragment key={row.company_code}>
                          {/* Company summary row — clickable */}
                          <tr
                            style={{ background: isOpen ? "#edf2ff" : i % 2 === 0 ? "#fff" : "#fafbfd", cursor: "pointer", transition: "background 0.15s" }}
                            onClick={() => {
                              if (isOpen) {
                                setExpandedCompanyCode(null);
                                setCompanyReports([]);
                              } else {
                                setExpandedCompanyCode(row.company_code);
                                loadCompanyReports(row.company_code);
                              }
                            }}
                          >
                            <td style={S.td}><code style={{ background: "#edf2ff", color: C.blue, padding: "2px 8px", borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{row.company_code || "—"}</code></td>
                            <td style={S.td}><strong>{row.company_name || "—"}</strong></td>
                            <td style={S.td}><span style={{ fontWeight: 800, fontSize: 18, color: C.navy }}>{row.total}</span></td>
                            <td style={S.td}><span style={{ ...S.tag("#1565c0"), padding: "3px 10px", fontSize: 12 }}>{row.preliminary}</span></td>
                            <td style={S.td}><span style={{ ...S.tag(C.success), padding: "3px 10px", fontSize: 12 }}>{row.final}</span></td>
                            <td style={{ ...S.td, color: C.muted, fontSize: 12 }}>{row.last_activity?.slice(0,16).replace("T"," ") || "—"}</td>
                            <td style={{ ...S.td, textAlign: "center", color: C.blue, fontSize: 13, fontWeight: 700 }}>
                              {isOpen ? "▲ Hide" : "▼ Reports"}
                            </td>
                          </tr>

                          {/* Expanded report list */}
                          {isOpen && (
                            <tr>
                              <td colSpan={7} style={{ padding: 0, background: "#f0f4ff", borderBottom: `2px solid ${C.blue}33` }}>
                                {companyReportsLoading ? (
                                  <div style={{ padding: "20px 32px", color: C.muted, fontSize: 13 }}>Loading reports…</div>
                                ) : companyReports.length === 0 ? (
                                  <div style={{ padding: "20px 32px", color: C.muted, fontSize: 13, fontStyle: "italic" }}>No reports found for this company.</div>
                                ) : (
                                  <div style={{ overflowX: "auto" }}>
                                    <table style={{ ...S.table, fontSize: 12 }}>
                                      <thead>
                                        <tr style={{ background: "#e8edf8" }}>
                                          {["#", "Client / Owner", "Bank", "Branch", "Type", "Visit Date", "Report Date", "Created By", "Last Saved"].map(h => (
                                            <th key={h} style={{ ...S.th, background: "#e8edf8", fontSize: 10, padding: "8px 12px" }}>{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {companyReports.map((r, ri) => (
                                          <tr key={r.id} style={{ background: ri % 2 === 0 ? "#fff" : "#f8faff" }}>
                                            <td style={{ ...S.td, fontSize: 11, color: C.muted, padding: "8px 12px" }}>{r.id}</td>
                                            <td style={{ ...S.td, fontWeight: 500, padding: "8px 12px", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.client_name || "—"}</td>
                                            <td style={{ ...S.td, padding: "8px 12px", whiteSpace: "nowrap" }}>{r.bank || "—"}</td>
                                            <td style={{ ...S.td, padding: "8px 12px" }}>{r.branch || "—"}</td>
                                            <td style={{ ...S.td, padding: "8px 12px" }}>
                                              <span style={{
                                                background: r.report_type === "preliminary" ? "#e8f0fe" : "#e8f5e9",
                                                color: r.report_type === "preliminary" ? "#1565c0" : C.success,
                                                borderRadius: 5, padding: "2px 8px", fontSize: 10, fontWeight: 700, whiteSpace: "nowrap",
                                              }}>{r.report_type}</span>
                                            </td>
                                            <td style={{ ...S.td, fontSize: 11, color: C.muted, padding: "8px 12px", whiteSpace: "nowrap" }}>{r.visit_date || "—"}</td>
                                            <td style={{ ...S.td, fontSize: 11, color: C.muted, padding: "8px 12px", whiteSpace: "nowrap" }}>{r.report_date || "—"}</td>
                                            <td style={{ ...S.td, padding: "8px 12px", fontSize: 12, fontWeight: 600, color: C.navy }}>
                                              {r.created_by || <span style={{ color: C.muted }}>—</span>}
                                            </td>
                                            <td style={{ ...S.td, fontSize: 11, color: C.muted, padding: "8px 12px", whiteSpace: "nowrap" }}>{r.updated_at?.slice(0,16).replace("T"," ") || "—"}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    <div style={{ padding: "8px 16px", fontSize: 11, color: C.muted, background: "#edf2ff", borderTop: `1px solid ${C.border}` }}>
                                      {companyReports.length} report{companyReports.length !== 1 ? "s" : ""} for {row.company_name || row.company_code}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {companyBreakdownSearch && stats.perCompany.filter(r => {
                  const q = companyBreakdownSearch.toLowerCase().trim();
                  return (r.company_code||"").toLowerCase().includes(q) || (r.company_name||"").toLowerCase().includes(q);
                }).length === 0 && (
                  <div style={{ padding: "20px 24px", textAlign: "center", color: C.muted, fontSize: 13, fontStyle: "italic" }}>
                    No companies match "{companyBreakdownSearch}"
                  </div>
                )}
              </div>

              <div style={S.section}>
                <div style={S.sectionHead}><h2 style={S.sectionTitle}>🕐 Recent Reports (last 20)</h2></div>
                <table style={S.table}>
                  <thead><tr>{["#","Company","Client","Bank","Branch","Type","Date","Saved"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {stats.recent.length === 0 ? (
                      <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: C.muted, padding: 32 }}>No reports yet.</td></tr>
                    ) : stats.recent.map((r, i) => (
                      <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfd" }}>
                        <td style={{ ...S.td, color: C.muted, fontSize: 11 }}>{r.id}</td>
                        <td style={S.td}><code style={{ background: "#edf2ff", color: C.blue, padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>{r.company_code || "—"}</code></td>
                        <td style={{ ...S.td, fontWeight: 500 }}>{r.client_name || "—"}</td>
                        <td style={S.td}>{r.bank || "—"}</td>
                        <td style={S.td}>{r.branch || "—"}</td>
                        <td style={S.td}><span style={{ ...S.tag(r.report_type === "preliminary" ? "#1565c0" : C.success), fontSize: 11 }}>{r.report_type}</span></td>
                        <td style={{ ...S.td, fontSize: 12 }}>{r.report_date || "—"}</td>
                        <td style={{ ...S.td, color: C.muted, fontSize: 11 }}>{r.updated_at?.slice(0,16).replace("T"," ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                <table style={S.table}>
                  <thead><tr>{["#","Company","Client","Bank","Branch","Type","Date","Saved"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {stats.recent.length === 0 ? (
                      <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: C.muted, padding: 32 }}>No reports yet.</td></tr>
                    ) : stats.recent.map((r, i) => (
                      <tr key={r.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfd" }}>
                        <td style={{ ...S.td, color: C.muted, fontSize: 11 }}>{r.id}</td>
                        <td style={S.td}><code style={{ background: "#edf2ff", color: C.blue, padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>{r.company_code || "—"}</code></td>
                        <td style={{ ...S.td, fontWeight: 500 }}>{r.client_name || "—"}</td>
                        <td style={S.td}>{r.bank || "—"}</td>
                        <td style={S.td}>{r.branch || "—"}</td>
                        <td style={S.td}><span style={{ ...S.tag(r.report_type === "preliminary" ? "#1565c0" : C.success), fontSize: 11 }}>{r.report_type}</span></td>
                        <td style={{ ...S.td, fontSize: 12 }}>{r.report_date || "—"}</td>
                        <td style={{ ...S.td, color: C.muted, fontSize: 11 }}>{r.updated_at?.slice(0,16).replace("T"," ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ══════════════ Map Tab ══════════════ */}
        {tab === "map" && (
          <div style={{ margin: "-28px -32px", height: "calc(100vh - 180px)" }}>
            <ContourMap user={user} companies={companies} />
          </div>
        )}
      </div>

      {/* ══════════════ Modals ══════════════ */}

      {modal === "addCompany" && (
        <Modal title="Add New Company" onClose={closeModal} maxWidth={560}>
          {error && <div style={{ background: "#fdecea", color: C.danger, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>{error}</div>}
          <form onSubmit={saveCompany}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <FL label="Company Name" col={2}><Input value={form.company_name || ""} onChange={set("company_name")} placeholder="e.g. Himalayan Bank Ltd" /></FL>
              <FL label="Company Code (auto-generated if blank)" col={2}><Input value={form.company_code || ""} onChange={set("company_code")} placeholder="e.g. HBL001" /></FL>
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, margin: "14px 0 12px", paddingTop: 14 }}>
              <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.5px" }}>Initial Admin Account</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <FL label="Admin Username *"><Input value={form.admin_username || ""} onChange={set("admin_username")} required placeholder="Admin login name" /></FL>
                <FL label="Admin Temp Password *"><PasswordInput value={form.admin_password || ""} onChange={set("admin_password")} required placeholder="Min 6 chars" /></FL>
              </div>
            </div>
            <p style={{ fontSize: 12, color: C.muted, margin: "0 0 16px" }}>Admin must change this password on first login.</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" style={S.outlineBtn} onClick={closeModal}>Cancel</button>
              <button type="submit" style={S.btn()} disabled={saving}>{saving ? "Creating…" : "Create Company"}</button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "editCompany" && (
        <Modal title={`Edit: ${target?.company_code}`} onClose={closeModal} maxWidth={640}>
          {error && <div style={{ background: "#fdecea", color: C.danger, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>{error}</div>}
          <form onSubmit={saveCompany}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <FL label="Company Name" col={2}><Input value={form.company_name || ""} onChange={set("company_name")} /></FL>
              <FL label="Address Line 1" col={2}><Input value={form.address1 || ""} onChange={set("address1")} placeholder="Street address" /></FL>
              <FL label="Address Line 2" col={2}><Input value={form.address2 || ""} onChange={set("address2")} placeholder="Suite, floor, etc." /></FL>
              <FL label="City"><Input value={form.city || ""} onChange={set("city")} /></FL>
              <FL label="State / Province"><Input value={form.state || ""} onChange={set("state")} /></FL>
              <FL label="ZIP / Postal Code"><Input value={form.zip || ""} onChange={set("zip")} /></FL>
              <FL label="Country"><Input value={form.country || ""} onChange={set("country")} /></FL>
              <FL label="Contact Email"><Input type="email" value={form.contact_email || ""} onChange={set("contact_email")} /></FL>
              <FL label="Contact Phone"><Input type="tel" value={form.contact_phone || ""} onChange={set("contact_phone")} /></FL>
            </div>

            {/* ── Letterhead PNG ── */}
            <div style={{ marginTop: 18, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
                🖼 Cover Letter Letterhead (PNG)
              </div>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                Upload your letterhead PNG. Then <strong>drag on the preview</strong> to define the text area box — the cover letter text will be placed exactly within that box.
              </p>

              {/* Upload + Remove buttons */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px", background: "#f0f4ff", border: `1.5px solid ${C.blue}`, color: C.blue, borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  📎 Choose PNG / JPG
                  <input type="file" accept="image/png,image/jpeg,image/jpg" style={{ display: "none" }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = ev => setForm(f => ({ ...f, letterhead_png: ev.target.result, letterhead_text_box: null }));
                      reader.readAsDataURL(file);
                      e.target.value = "";
                    }}
                  />
                </label>
                {form.letterhead_png && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, letterhead_png: "", letterhead_text_box: null }))}
                    style={{ padding: "9px 14px", background: "#fff", color: C.danger, border: `1px solid ${C.danger}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    ✕ Remove
                  </button>
                )}
                {form.letterhead_text_box && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, letterhead_text_box: null }))}
                    style={{ padding: "9px 14px", background: "#fff", color: C.warn, border: `1px solid ${C.warn}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    ↺ Reset Box
                  </button>
                )}
              </div>

              {/* Interactive A4 preview with drag-to-define text box */}
              {form.letterhead_png ? (
                <LetterheadBoxDefiner
                  png={form.letterhead_png}
                  box={form.letterhead_text_box}
                  onChange={box => setForm(f => ({ ...f, letterhead_text_box: box }))}
                />
              ) : (
                <div style={{ padding: "20px", border: `1.5px dashed ${C.border}`, borderRadius: 8, textAlign: "center", color: C.muted, fontSize: 13, fontStyle: "italic" }}>
                  No letterhead uploaded yet
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
              <button type="button" style={S.outlineBtn} onClick={closeModal}>Cancel</button>
              <button type="submit" style={S.btn()} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "deleteCompany" && (
        <Modal title="Delete Company" onClose={closeModal}>
          {error && <div style={{ background: "#fdecea", color: C.danger, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>{error}</div>}
          <p style={{ fontSize: 15, color: C.text }}>Permanently delete <strong>{target?.company_code}</strong>{target?.company_name ? ` (${target.company_name})` : ""}?</p>
          <p style={{ color: C.danger, fontSize: 13 }}>⚠ This deletes ALL users and data for this company. Cannot be undone.</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" style={S.outlineBtn} onClick={closeModal}>Cancel</button>
            <button style={S.btn(GRAD.danger)} onClick={deleteCompany} disabled={saving}>{saving ? "Deleting…" : "Delete Permanently"}</button>
          </div>
        </Modal>
      )}

      {(modal === "addUser" || modal === "editUser") && (
        <Modal title={modal === "addUser" ? "Add User" : `Edit: ${target?.username}`} onClose={closeModal}>
          {error && <div style={{ background: "#fdecea", color: C.danger, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>{error}</div>}
          <form onSubmit={saveUser}>
            {modal === "addUser" && (<>
              <FL label="Company *"><Select value={form.company_code || ""} onChange={set("company_code")} required>
                <option value="">Select company…</option>
                {companies.map(c => <option key={c.id} value={c.company_code}>{c.company_code} — {c.company_name}</option>)}
              </Select></FL>
              <FL label="Role *"><Select value={form.role || "user"} onChange={set("role")} required>
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="super_user">Super User</option>
              </Select></FL>
            </>)}
            <FL label="Username *"><Input value={form.username || ""} onChange={set("username")} required /></FL>
            <FL label="Email"><Input type="email" value={form.email || ""} onChange={set("email")} placeholder="optional" /></FL>
            <FL label={modal === "editUser" ? "New Password (blank = keep)" : "Temporary Password *"}>
              <PasswordInput value={form.password || ""} onChange={set("password")} required={modal === "addUser"} placeholder="Min 6 characters" />
            </FL>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" style={S.outlineBtn} onClick={closeModal}>Cancel</button>
              <button type="submit" style={S.btn()} disabled={saving}>{saving ? "Saving…" : modal === "addUser" ? "Create User" : "Save"}</button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "deleteUser" && (
        <Modal title="Delete User" onClose={closeModal}>
          {error && <div style={{ background: "#fdecea", color: C.danger, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 14 }}>{error}</div>}
          <p style={{ fontSize: 15 }}>Delete user <strong>{target?.username}</strong> from <strong>{target?.company_code}</strong>?</p>
          <p style={{ color: C.danger, fontSize: 13 }}>⚠ This cannot be undone.</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" style={S.outlineBtn} onClick={closeModal}>Cancel</button>
            <button style={S.btn(GRAD.danger)} onClick={deleteUser} disabled={saving}>{saving ? "Deleting…" : "Delete"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
